
import _debounce from 'lodash/debounce';
import { defineComponent, ref, reactive, computed, watch } from 'vue';

import {
  CatalogSearchTypeEnum,
  ClassificationsBrowserAutocompleteResult,
} from '@/common/services/swagger/index.defs';
import { SearchCommand } from '@/classifications/api/runtime/CommandExecutor';
import { setupComponent } from '@/classifications/composables/setupComponent';
import { ComponentName } from '@/classifications/api/configuration/components/ComponentName';

import Highlighter from 'vue-highlight-words';
import Btn from '@/common/components/Btn.vue';
import Spinner from '@/common/components/Spinner.vue';
import Dropdown from '@/common/components/Dropdown.vue';
import Clickable from '@/common/components/Clickable.vue';
import InjectStyles from '@/common/components/InjectStyles.vue';
import FormInputClearable from '@/common/components/FormInputClearable.vue';

interface AutocompleteModelItem {
  text?: string;
  count?: number;
  type: 'item' | 'root' | 'current';
}

const searchTypeCatalogName = {
  value: CatalogSearchTypeEnum.CatalogCodeAndDescription,
  translate: 'SearchByCodeAndDescription',
};

const searchTypeCreatedBy = {
  value: CatalogSearchTypeEnum.CreatedBy,
  translate: 'SearchByCreatedBy',
};

interface Autocomplete {
  totalCount: number;
  visibleMatchCount: number;
  items: AutocompleteModelItem[];
}

export default defineComponent({
  name: ComponentName.searchForm,

  components: {
    Btn,
    Spinner,
    Dropdown,
    Clickable,
    Highlighter,
    InjectStyles,
    FormInputClearable,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();

    const searchModel = reactive({
      text: '',
      type: CatalogSearchTypeEnum.CatalogCodeAndDescription,
      isDirty: false,
    });

    const autocomplete = ref<Autocomplete>({
      totalCount: 0,
      visibleMatchCount: 0,
      items: [],
    });
    const isAutocompleteVisible = ref(false);
    const isAutocompleteLoading = ref(false);

    const {
      componentName,
      isReady,
      isWebComponent,
      instance,
      store,
      routeData,
      widerThan,
      narrowerThan,
      t,
    } = setupComponent(root, props.instanceId);

    const visible = computed(() => store.value?.options.components?.searchForm?.visible);

    const autocompleteEnabled = computed(
      () => store.value?.options.components?.searchForm?.useAutocomplete,
    );

    const baseSearchTypeOptions = [searchTypeCatalogName, searchTypeCreatedBy];

    const searchTypeOptions = computed(() => {
      return baseSearchTypeOptions;
    });

    const onSearch = async () => {
      isAutocompleteVisible.value = false;
      try {
        await instance.value?.execute(
          new SearchCommand({
            searchText: searchModel.text,
          }),
        );

        searchModel.isDirty = false;
      } catch (error) {
        // Ignore Error
      }
    };

    const searchText = (text: string) => {
      searchModel.text = text;
      onSearch();
    };

    const onClear = () => {
      if (!searchModel.isDirty) {
        searchModel.text = '';
        autocomplete.value.items = [];
        autocomplete.value.totalCount = 0;
        onSearch();
      }
    };

    const openAutocomplete = () => {
      if (!autocompleteEnabled.value) {
        return;
      }

      if (searchModel.text == routeData.value?.searchText) {
        return;
      }

      if (searchModel.text.length > 2) {
        isAutocompleteVisible.value = true;
      }
    };

    const autocompleteHighlightTerms = ref<string[]>([]);

    const closeAutocomplete = () => {
      isAutocompleteVisible.value = false;
    };

    const handleAutocompleteSearch = _debounce(async (text: string) => {
      {
        let items: AutocompleteModelItem[] = [];
        let totalCount = 0;
        let visibleMatchCount = 0;
        const searchTextTrimmed = (text || '').trim();
        if (autocompleteEnabled.value && searchTextTrimmed.length > 2) {
          isAutocompleteLoading.value = true;
          autocompleteHighlightTerms.value = searchTextTrimmed
            .toLowerCase()
            .split(/[._-\s/]+/)
            .concat([searchTextTrimmed]);
          try {
            const result: ClassificationsBrowserAutocompleteResult | undefined =
              await instance.value?.httpService?.autocomplete(text);
            items = (result?.matches || []).map((match: string) => ({
              text: match,
              type: 'item',
            }));
            totalCount = result?.totalCount || 0;
            visibleMatchCount = result?.matches?.length || 0;
          } catch (error) {
            // Ignore Error
          }
        }
        autocomplete.value = { items, totalCount, visibleMatchCount };
        isAutocompleteVisible.value = autocomplete.value.items.length > 0;
        isAutocompleteLoading.value = false;
      }
    }, 600);

    const onSearchInput = () => {
      searchModel.isDirty =
        searchModel.text?.length > 0 && searchModel.text != routeData.value?.searchText;
      handleAutocompleteSearch(searchModel.text);
    };

    // Sync the search model text with route search text after initializing the app.
    watch(
      () => routeData.value?.searchText,
      (text, oldText) => {
        if (text !== oldText) {
          searchModel.text = text ?? '';
        }
      },
    );

    return {
      root,
      componentName,
      isReady,
      isWebComponent,
      store,
      searchModel,
      searchTypeOptions,
      autocomplete,
      isAutocompleteVisible,
      isAutocompleteLoading,
      autocompleteHighlightTerms,
      widerThan,
      narrowerThan,
      t,
      onSearch,
      onSearchInput,
      onClear,
      searchText,
      openAutocomplete,
      closeAutocomplete,
      visible,
    };
  },
});
